import { useQuery } from "@tanstack/react-query";
import React from "react";
import AdminService from "../service/admin.service";
import { MaterialReactTable } from "material-react-table";
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Autocomplete,
} from "@mui/material";
import Drawer from "modules/UI/components/Drawer.component";
import {
  Check,
  Close,
  DeleteForever,
  Edit,
  Palette,
} from "@mui/icons-material";
import { useConfirmation } from "modules/UI/hooks/useConfirmation.hook";
import PUIRatioBox from "modules/UI/components/RatioBox.component";
import { MuiChipsInput } from "lib/mui-chips-input";
import { useStaticData } from "modules/MasterData/service/staticData.hooks";

export const PromoCategoriesContainer = () => {
  const deleteConfirmation = useConfirmation({
    title: "Delete Sub Category",
  });

  const [selectedCategory, setSelectedCategory] = React.useState(null);
  const { data, refetch, isLoading, isFetching } = useQuery({
    queryKey: ["promo-categories/get-all"],
    queryFn: async () => {
      const response = await AdminService.getAllPromoCategories();
      return response;
    },
    initialData: [],
  });
  const { spendCategoriesHashMap, promoCategoryTypes } = useStaticData();
  const l3SpendCategories = Object.values(spendCategoriesHashMap).filter(
    (spendCat: any) => spendCat.active && spendCat.level === 3
  );

  return (
      <div>
        {deleteConfirmation.JSX}
        <Drawer
            open={!!selectedCategory}
            onClose={() => setSelectedCategory(null)}
            title={
              selectedCategory?.id ? (
                  <>
                    Edit <em>{selectedCategory?.name}</em>
                  </>
              ) : (
                  "Create new Sub category"
              )
            }
            footerContent={
              <Grid container spacing={2} justifyContent={"flex-end"}>
                <Grid item>
                  <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setSelectedCategory(null)}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                      variant="contained"
                      color="primary"
                      disabled={!selectedCategory?.name?.trim() || !selectedCategory?.spendCategoryL3 || selectedCategory?.promoCategoryType === undefined}
                      onClick={() => {
                        AdminService[
                            selectedCategory.id
                                ? "updatePromoCategory"
                                : "createPromoCategory"
                            ](selectedCategory)
                            .then(() => {
                              refetch();
                              setSelectedCategory(null);
                            })
                            .catch((e) => {
                              console.error(e);
                            });
                      }}
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            }
        >
          <Box sx={{m: -2, mb: 2}}>
            <PUIRatioBox xRatio={16} yRatio={9}>
              <Avatar
                  variant="square"
                  sx={{
                    width: "100%",
                    height: "100%",
                    // background: "linear-gradient(112.5deg, rgb(214, 214, 214) 0%, rgb(214, 214, 214) 10%,rgb(195, 195, 195) 10%, rgb(195, 195, 195) 53%,rgb(176, 176, 176) 53%, rgb(176, 176, 176) 55%,rgb(157, 157, 157) 55%, rgb(157, 157, 157) 60%,rgb(137, 137, 137) 60%, rgb(137, 137, 137) 88%,rgb(118, 118, 118) 88%, rgb(118, 118, 118) 91%,rgb(99, 99, 99) 91%, rgb(99, 99, 99) 100%),linear-gradient(157.5deg, rgb(214, 214, 214) 0%, rgb(214, 214, 214) 10%,rgb(195, 195, 195) 10%, rgb(195, 195, 195) 53%,rgb(176, 176, 176) 53%, rgb(176, 176, 176) 55%,rgb(157, 157, 157) 55%, rgb(157, 157, 157) 60%,rgb(137, 137, 137) 60%, rgb(137, 137, 137) 88%,rgb(118, 118, 118) 88%, rgb(118, 118, 118) 91%,rgb(99, 99, 99) 91%, rgb(99, 99, 99) 100%),linear-gradient(135deg, rgb(214, 214, 214) 0%, rgb(214, 214, 214) 10%,rgb(195, 195, 195) 10%, rgb(195, 195, 195) 53%,rgb(176, 176, 176) 53%, rgb(176, 176, 176) 55%,rgb(157, 157, 157) 55%, rgb(157, 157, 157) 60%,rgb(137, 137, 137) 60%, rgb(137, 137, 137) 88%,rgb(118, 118, 118) 88%, rgb(118, 118, 118) 91%,rgb(99, 99, 99) 91%, rgb(99, 99, 99) 100%),linear-gradient(90deg, rgb(195, 195, 195),rgb(228, 228, 228)); background-blend-mode:overlay,overlay,overlay,multiply",
                    backgroundColor: selectedCategory?.color || "#ccc",
                    backgroundImage:
                        "linear-gradient(0deg, rgba(0,0,0,1), rgba(255,255,255,.1))",
                    backgroundBlendMode: "overlay",
                    "& div": {
                      opacity: 0.2,
                      width: "50%",
                      height: "50%",
                      "& svg": {
                        width: "100%",
                        height: "100%",
                      },
                    },
                  }}
              >
                {selectedCategory ? (
                    <div
                        dangerouslySetInnerHTML={{__html: selectedCategory?.icon}}
                    />
                ) : (
                    <></>
                )}
              </Avatar>
            </PUIRatioBox>
          </Box>
          <Stack spacing={2}>
            <Autocomplete
                fullWidth
                options={l3SpendCategories}
                getOptionLabel={(option: any) => `[${option.code}] ${option.name}`}
                value={
                    l3SpendCategories.find(
                        (cat: any) => cat.id === selectedCategory?.spendCategoryL3?.id
                    ) || null
                }
                onChange={(_, newValue) => {
                  setSelectedCategory({
                    ...selectedCategory,
                    spendCategoryL3: newValue,
                  });
                }}
                renderInput={(params) => (
                    <TextField {...params} label="Spend Category L3"/>
                )}
            />
            <TextField
                fullWidth
                label="Sub Type Name"
                required
                value={selectedCategory?.name || ""}
                onChange={(e) => {
                  setSelectedCategory({
                    ...selectedCategory,
                    name: e.target.value.toUpperCase(),
                  });
                }}
                error={!selectedCategory?.name?.trim()}
                helperText={!selectedCategory?.name?.trim() ? "Sub Type Name is required" : ""}
            />
            <FormControl fullWidth required>
              <InputLabel id="type-select-label">Type</InputLabel>
              <Select
                  labelId="type-select-label"
                  label="Type"
                  displayEmpty
                  value={selectedCategory?.promoCategoryType ?? ""}
                  onChange={(e) => {
                    setSelectedCategory({
                      ...selectedCategory,
                      promoCategoryType: e.target.value,
                    });
                  }}
                  error={selectedCategory?.promoCategoryType === undefined}
              >
                <MenuItem value="" disabled></MenuItem>
                {promoCategoryTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.code} - {type.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormGroup>
              <FormControlLabel
                  control={
                    <Switch
                        checked={selectedCategory?.qualityControl}
                        onChange={(e) => {
                          setSelectedCategory({
                            ...selectedCategory,
                            qualityControl: e.target.checked,
                          });
                        }}
                    />
                  }
                  label="Requires Quality Control"
              />
            </FormGroup>
            <MuiChipsInput
                value={selectedCategory?.requiredDocuments || []}
                onChange={(newValue) =>
                    setSelectedCategory({
                      ...selectedCategory,
                      requiredDocuments: newValue,
                    })
                }
                size="small"
                fullWidth
                label={`Required quality control documents`}
            />
            <ListItem sx={{p: 0}}>
              <ListItemText
                  primary="Color:"
                  secondary={
                    <input
                        style={{width: "100%"}}
                        type="color"
                        value={selectedCategory?.color}
                        onChange={(e) =>
                            setSelectedCategory({
                              ...selectedCategory,
                              color: e.target.value,
                            })
                        }
                    />
                  }
              />
            </ListItem>
            <ListItem sx={{p: 0}}>
              {!!selectedCategory && (
                  <ListItemIcon
                      dangerouslySetInnerHTML={{__html: selectedCategory?.icon}}
                  />
              )}
              <ListItemText
                  primary="Icon:"
                  secondary={
                    <input
                        type="file"
                        accept={"image/svg+xml"}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          const reader = new FileReader();
                          reader.onload = () => {
                            setSelectedCategory({
                              ...selectedCategory,
                              icon: reader.result,
                            });
                          };
                          reader.readAsText(file);
                        }}
                    />
                  }
              />
            </ListItem>
          </Stack>
        </Drawer>
        <MaterialReactTable
            // enableGrouping
            muiTablePaperProps={({table}) => ({
              style: {background: "white !important"},
              position: "relative",
              zIndex: table.getState().isFullScreen ? 1300 : undefined,
            })}
            state={{
              isLoading: isLoading,
              showProgressBars: isFetching,
            }}
            initialState={{
              //   grouping: ["promoCategoryType"],
              pagination: {
                pageIndex: 0,
                pageSize: 50,
              },
              columnPinning: {
                left: ["mrt-row-expand", "mrt-row-select"],
                right: ["mrt-row-actions"],
              },
            }}
            data={data}
            enableRowActions
            renderTopToolbarCustomActions={() => {
              return (
                  <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setSelectedCategory({})}
                  >
                    Add Sub Category
                  </Button>
              );
            }}
            renderRowActionMenuItems={({row, closeMenu}) => {
              return [
                <MenuItem
                    key="edit"
                    disabled={row.original.isDeleted}
                    onClick={() => {
                      setSelectedCategory(row.original);
                      closeMenu();
                    }}
                >
                  <ListItemIcon>
                    <Edit fontSize="small"/>
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>,
                <MenuItem
                    key="delete"
                    disabled={row.original.isDeleted}
                    onClick={() => {
                      closeMenu();
                      deleteConfirmation
                          .requestConfirmation()
                          .then((reason) => {
                            AdminService.deletePromoCategory({
                              id: row.original.id,
                            }).then(() => {
                              refetch();
                            });
                          })
                          .catch(() => {
                          });
                    }}
                >
                  <ListItemIcon>
                    <DeleteForever fontSize="small"/>
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>,
              ];
            }}
            columns={[
              {
                accessorKey: "id",
                header: "ID",
              },
              {
                accessorKey: "spendCategoryL3.name",
                header: "Category L3",
                Cell: ({row}) => {
                  const category = row.original.spendCategoryL3;
                  return category ? `[${category.code}] ${category.name}` : "No category";
                },
              },
              {
                accessorKey: "promoCategoryType",
                header: "Type",
                size: 200,
                Cell: ({row}) => {
                  const type = promoCategoryTypes.find(t => t.id === row.original.promoCategoryType);
                  return type ? `${type.code} - ${type.description}` : "Unknown type";
                },
              },
              {
                accessorKey: "name",
                header: "Sub Type Name",
                Cell: ({row}) => {
                  return `${row.original.isDeleted ? "[DELETED] " : ""}${
                      row.original.name
                  }`;
                },
              },
              {
                accessorKey: "qualityControl",
                header: "Quality Control",
                Cell: ({row}) => {
                  return row.original.qualityControl ? (
                      <Chip icon={<Check/>} color="success" label="Yes"/>
                  ) : (
                      <Chip icon={<Close/>} color="error" label="No"/>
                  );
                },
              },
              {
                accessorKey: "requiredDocuments",
                header: "Quality Documents",
                Cell: ({row}) => {
                  return row.original.requiredDocuments?.join(", ");
                },
              },
              {
                accessorKey: "color",
                header: "Color",
                Cell: ({row}) => {
                  return (
                      <Chip
                          icon={<Palette/>}
                          label={row.original.color || "No color"}
                          style={{backgroundColor: row.original.color}}
                      />
                  );
                },
              },
              {
                accessorKey: "icon",
                header: "Icon",
                Cell: ({row}) => {
                  return (
                      <div
                          style={{width: 100}}
                          dangerouslySetInnerHTML={{__html: row.original.icon}}
                      />
                  );
                },
              },
            ]}
        />
      </div>
  );
};

export default PromoCategoriesContainer;
